import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { RotateCcw } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';
import useEditorAutoGrow from './useEditorAutoGrow';

const getMediaUrl = (src) => resolveMediaUrl(src) || '';

const shuffleWithSeed = (array, seed) => {
  if (array.length <= 1) return [...array];
  const seededRandom = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };
  for (let attempt = 0; attempt < 20; attempt++) {
    const result = [...array];
    let seedValue = seed + attempt * 1000;
    for (let i = result.length - 1; i > 0; i--) {
      seedValue++;
      const j = Math.floor(seededRandom(seedValue) * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    if (result.every((v, i) => v !== array[i])) return result;
  }
  return [...array.slice(1), array[0]];
};

const LabelImageElement = ({
  element,
  isEditor = false,
  studentView = false,
  connections: externalConnections = null,
  showResult = false,
  perItemResults = null,
  onConnectionsChange,
  onCheck,
  onRetry,
  disabled = false,
  hideCheckButton = false,
  hideActionBar = false,
  slideScale = 1,
  onAutoGrow,
}) => {
  const { t } = useTranslation();
  const { content, style, id } = element;
  const {
    imageUrl = null,
    items = [],
    allowRetry = false,
    lineColor = '#2A4D25',
    dotFillColor = '#ffffff',
    dotBorderColor = '#2A4D25',
    dotSize = 18,
    fontSize = 18,
    fontFamily = 'Inter',
  } = content || {};
  const backgroundColor = style?.backgroundColor || '#ffffff';

  const s = typeof slideScale === 'number' && slideScale > 0 ? slideScale : 1;
  const scaledFontSize = Math.max(8, fontSize * s);
  const scaledDot = Math.max(8, Math.round(dotSize * s));
  const scaledPad = Math.max(6, Math.round(16 * s));
  const scaledGapY = Math.max(2, Math.round(12 * s));
  const scaledStroke = Math.max(1.5, 3 * s);
  const scaledItemPadX = Math.max(4, Math.round(14 * s));
  const scaledItemPadY = Math.max(2, Math.round(8 * s));
  const scaledItemGap = Math.max(4, Math.round(10 * s));
  const scaledConnector = Math.max(6, Math.round(12 * s));

  const leftItems = useMemo(() => items.filter((it) => it.side !== 'right'), [items]);
  const rightItems = useMemo(() => items.filter((it) => it.side === 'right'), [items]);

  const leftOrder = useMemo(() => {
    if (leftItems.length === 0) return [];
    const ids = leftItems.map((it) => it.id);
    const seed = id ? id.split('').reduce((a, c) => a + c.charCodeAt(0), 17) : 1;
    return shuffleWithSeed(ids, seed);
  }, [leftItems, id]);

  const rightOrder = useMemo(() => {
    if (rightItems.length === 0) return [];
    const ids = rightItems.map((it) => it.id);
    const seed = id ? id.split('').reduce((a, c) => a + c.charCodeAt(0), 53) : 2;
    return shuffleWithSeed(ids, seed);
  }, [rightItems, id]);

  // connections: { [labelItemId]: dotItemId | null }
  const [connections, setConnections] = useState(externalConnections || {});
  useEffect(() => {
    if (externalConnections) setConnections(externalConnections);
  }, [externalConnections]);

  // selected: { side: 'label' | 'dot', id } | null
  const [selected, setSelected] = useState(null);

  const updateConnections = useCallback((next) => {
    setConnections(next);
    onConnectionsChange?.(next);
  }, [onConnectionsChange]);

  const interactive = !isEditor && !showResult && !disabled && items.length > 0;

  const createConnection = (labelId, dotId) => {
    const next = { ...connections };
    Object.keys(next).forEach((lid) => {
      if (next[lid] === dotId) next[lid] = null;
    });
    next[labelId] = dotId;
    updateConnections(next);
    setSelected(null);
  };

  const handleLabelClick = (itemId) => {
    if (!interactive) return;
    const isConnected = !!connections[itemId];
    if (isConnected) {
      updateConnections({ ...connections, [itemId]: null });
      setSelected(null);
      return;
    }
    if (selected && selected.side === 'label' && selected.id === itemId) {
      setSelected(null);
      return;
    }
    if (selected && selected.side === 'dot') {
      createConnection(itemId, selected.id);
      return;
    }
    setSelected({ side: 'label', id: itemId });
  };

  const handleDotClick = (itemId) => {
    if (!interactive) return;
    const connectedLabelId = Object.keys(connections).find((lid) => connections[lid] === itemId);
    if (connectedLabelId) {
      updateConnections({ ...connections, [connectedLabelId]: null });
      setSelected(null);
      return;
    }
    if (selected && selected.side === 'dot' && selected.id === itemId) {
      setSelected(null);
      return;
    }
    if (selected && selected.side === 'label') {
      createConnection(selected.id, itemId);
      return;
    }
    setSelected({ side: 'dot', id: itemId });
  };

  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const imageWrapRef = useRef(null);
  const labelDotRefs = useRef({});
  const imageDotRefs = useRef({});
  const [dotPositions, setDotPositions] = useState({ label: {}, image: {} });

  useEditorAutoGrow({
    enabled: isEditor,
    scrollRef,
    currentHeightPct: element.size?.height || 0,
    contentCount: items.length,
    onAutoGrow,
  });

  useLayoutEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const offW = containerRef.current.offsetWidth || containerRect.width || 1;
      const offH = containerRef.current.offsetHeight || containerRect.height || 1;
      const scaleX = containerRect.width / offW || 1;
      const scaleY = containerRect.height / offH || 1;
      const label = {};
      const image = {};
      items.forEach((it) => {
        const ldot = labelDotRefs.current[it.id];
        if (ldot) {
          const r = ldot.getBoundingClientRect();
          label[it.id] = {
            x: (r.left + r.width / 2 - containerRect.left) / scaleX,
            y: (r.top + r.height / 2 - containerRect.top) / scaleY,
          };
        }
        const idot = imageDotRefs.current[it.id];
        if (idot) {
          const r = idot.getBoundingClientRect();
          image[it.id] = {
            x: (r.left + r.width / 2 - containerRect.left) / scaleX,
            y: (r.top + r.height / 2 - containerRect.top) / scaleY,
          };
        }
      });
      setDotPositions((prev) => {
        const same = (a, b) => {
          const ak = Object.keys(a);
          const bk = Object.keys(b);
          if (ak.length !== bk.length) return false;
          for (const k of ak) {
            if (!b[k]) return false;
            if (a[k].x !== b[k].x || a[k].y !== b[k].y) return false;
          }
          return true;
        };
        if (same(label, prev.label) && same(image, prev.image)) return prev;
        return { label, image };
      });
    };
    measure();
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      const ro = new ResizeObserver(measure);
      ro.observe(containerRef.current);
      if (imageWrapRef.current) ro.observe(imageWrapRef.current);
      return () => ro.disconnect();
    }
  }, [items, leftOrder, rightOrder, fontSize, s, imageUrl]);

  const allConnected = items.length > 0 && items.every((it) => connections[it.id]);
  const allCorrect = showResult && perItemResults && items.length > 0
    && items.every((it) => perItemResults[it.id] === true);

  const handleCheck = () => onCheck?.(connections);
  const handleRetry = () => {
    setConnections({});
    setSelected(null);
    onRetry?.();
  };

  const renderLabel = (item, columnSide) => {
    const isConnected = !!connections[item.id];
    const isSelected = selected && selected.side === 'label' && selected.id === item.id;
    const itemResult = showResult && perItemResults ? perItemResults[item.id] : null;
    let bg = item.color || '#4b5563';
    let txt = item.textColor || '#ffffff';
    if (itemResult === true) { bg = '#22c55e'; txt = '#ffffff'; }
    else if (itemResult === false) { bg = '#ef4444'; txt = '#ffffff'; }

    let dotBg = isConnected ? (item.color || dotBorderColor) : dotFillColor;
    let dotBorder = item.color || dotBorderColor;
    if (itemResult === true) { dotBg = '#22c55e'; dotBorder = '#16a34a'; }
    else if (itemResult === false) { dotBg = '#ef4444'; dotBorder = '#dc2626'; }
    if (isSelected) dotBorder = '#6366f1';

    const flexDir = columnSide === 'left' ? 'row' : 'row-reverse';
    return (
      <div className="flex items-center" style={{ flexDirection: flexDir, gap: `${scaledItemGap}px` }}>
        <button
          type="button"
          onClick={() => handleLabelClick(item.id)}
          disabled={!interactive}
          className={`flex-1 min-w-0 rounded-lg text-center transition-all ${
            interactive ? 'cursor-pointer hover:scale-[1.02]' : 'cursor-default'
          }`}
          style={{
            backgroundColor: bg,
            color: txt,
            fontSize: `${scaledFontSize}px`,
            fontFamily,
            padding: `${scaledItemPadY}px ${scaledItemPadX}px`,
            boxShadow: isSelected ? '0 0 0 2px rgba(99, 102, 241, 0.4)' : '0 1px 2px rgba(0,0,0,0.08)',
            border: 'none',
            minHeight: `${scaledFontSize + 14 * s}px`,
          }}
        >
          <span className="break-words">{item.text || <span className="opacity-40">—</span>}</span>
        </button>
        <div
          className="flex items-center"
          style={{ gap: 0, flexDirection: columnSide === 'left' ? 'row' : 'row-reverse' }}
        >
          <div
            style={{
              width: `${scaledConnector}px`,
              height: `${Math.max(2, 2 * s)}px`,
              backgroundColor: item.color || dotBorderColor,
              borderRadius: 2,
            }}
          />
          <button
            ref={(el) => { labelDotRefs.current[item.id] = el; }}
            type="button"
            onClick={() => handleLabelClick(item.id)}
            disabled={!interactive}
            className={`flex-shrink-0 rounded-full transition-all ${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'}`}
            style={{
              width: scaledDot,
              height: scaledDot,
              backgroundColor: dotBg,
              border: `${Math.max(1.5, 3 * s)}px solid ${dotBorder}`,
            }}
            aria-label={t('games.labelImage.labelDot', { defaultValue: 'Крапка мітки' })}
          />
        </div>
      </div>
    );
  };

  const renderImageDot = (item) => {
    const connectedLabelId = Object.keys(connections).find((lid) => connections[lid] === item.id);
    const isConnected = !!connectedLabelId;
    const isSelected = selected && selected.side === 'dot' && selected.id === item.id;
    const itemResult = showResult && perItemResults && connectedLabelId
      ? perItemResults[connectedLabelId]
      : null;
    let bg = isConnected ? dotBorderColor : dotFillColor;
    let border = dotBorderColor;
    if (itemResult === true) { bg = '#22c55e'; border = '#16a34a'; }
    else if (itemResult === false) { bg = '#ef4444'; border = '#dc2626'; }
    if (isSelected) border = '#6366f1';

    return (
      <button
        key={`imgdot_${item.id}`}
        ref={(el) => { imageDotRefs.current[item.id] = el; }}
        type="button"
        onClick={() => handleDotClick(item.id)}
        disabled={!interactive}
        className={`absolute rounded-full transition-all ${interactive ? 'cursor-pointer hover:scale-125' : 'cursor-default'}`}
        style={{
          left: `${item.x ?? 50}%`,
          top: `${item.y ?? 50}%`,
          width: scaledDot,
          height: scaledDot,
          transform: 'translate(-50%, -50%)',
          backgroundColor: bg,
          border: `${Math.max(1.5, 3 * s)}px solid ${border}`,
          boxShadow: isSelected
            ? `0 0 0 ${Math.max(1.5, 3 * s)}px rgba(99, 102, 241, 0.25), 0 1px 3px rgba(0,0,0,0.3)`
            : '0 1px 3px rgba(0,0,0,0.3)',
        }}
        aria-label={t('games.labelImage.imageDot', { defaultValue: 'Крапка на зображенні' })}
      />
    );
  };

  const hideChrome = !isEditor;

  const emptyState = items.length === 0 && !imageUrl;

  return (
    <div
      className={`w-full h-full rounded-xl overflow-hidden relative flex flex-col ${
        hideChrome ? '' : 'border-2 shadow-sm'
      }`}
      style={{
        backgroundColor: hideChrome ? 'transparent' : backgroundColor,
        borderColor: '#e5e7eb',
      }}
    >
      {emptyState ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400 p-4 text-center">
          {t('games.labelImage.addImageAndItems', { defaultValue: 'Додайте зображення та мітки у налаштуваннях' })}
        </div>
      ) : (
        <div ref={scrollRef} className="flex-1 relative overflow-auto" style={{ padding: `${scaledPad}px` }}>
          <div ref={containerRef} className="relative w-full h-full">
            <div
              className="grid items-stretch relative w-full h-full"
              style={{
                gridTemplateColumns: `minmax(0, 1fr) minmax(0, 2.2fr) minmax(0, 1fr)`,
                columnGap: `${Math.max(8, Math.round(16 * s))}px`,
                rowGap: `${scaledGapY}px`,
              }}
            >
              {/* Left column */}
              <div className="flex flex-col justify-center" style={{ gap: `${scaledGapY}px` }}>
                {leftOrder.map((iid) => {
                  const it = leftItems.find((x) => x.id === iid);
                  if (!it) return null;
                  return <div key={`left_${it.id}`}>{renderLabel(it, 'left')}</div>;
                })}
              </div>

              {/* Center image */}
              <div className="flex items-center justify-center min-h-0">
                {imageUrl ? (
                  <div
                    ref={imageWrapRef}
                    className="relative"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      display: 'inline-block',
                    }}
                  >
                    <img
                      src={getMediaUrl(imageUrl)}
                      alt=""
                      draggable={false}
                      className="block select-none"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                    <div className="absolute inset-0">
                      {items.map(renderImageDot)}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center text-xs text-gray-400 border border-dashed border-gray-300 rounded-lg w-full h-full p-4 text-center">
                    {t('games.labelImage.addImage', { defaultValue: 'Додайте зображення у налаштуваннях' })}
                  </div>
                )}
              </div>

              {/* Right column */}
              <div className="flex flex-col justify-center" style={{ gap: `${scaledGapY}px` }}>
                {rightOrder.map((iid) => {
                  const it = rightItems.find((x) => x.id === iid);
                  if (!it) return null;
                  return <div key={`right_${it.id}`}>{renderLabel(it, 'right')}</div>;
                })}
              </div>
            </div>

            {/* SVG overlay for connection lines */}
            <svg
              className="absolute inset-0 pointer-events-none"
              style={{ width: '100%', height: '100%' }}
            >
              {items.map((it) => {
                const dotId = connections[it.id];
                if (!dotId) return null;
                const a = dotPositions.label[it.id];
                const b = dotPositions.image[dotId];
                if (!a || !b) return null;
                let stroke = it.color || lineColor;
                if (showResult && perItemResults) {
                  stroke = perItemResults[it.id] === true ? '#22c55e' : '#ef4444';
                }
                return (
                  <line
                    key={`line_${it.id}`}
                    x1={a.x}
                    y1={a.y}
                    x2={b.x}
                    y2={b.y}
                    stroke={stroke}
                    strokeWidth={scaledStroke}
                    strokeLinecap="round"
                  />
                );
              })}
            </svg>
          </div>
        </div>
      )}

      {!isEditor && !hideActionBar && items.length > 0 && (
        <div className="absolute bottom-2 right-2 z-10 flex items-center gap-2">
          {showResult && allowRetry && !allCorrect && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg shadow-sm transition"
            >
              <RotateCcw size={14} />
              {t('games.common.tryAgain')}
            </button>
          )}
          {!showResult && !hideCheckButton && (
            <button
              onClick={handleCheck}
              disabled={!allConnected}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg shadow-sm transition ${
                allConnected
                  ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {t('games.common.check')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LabelImageElement;
